import fallbackUniversityDirectory from "./university-directory-fallback.json";

const universityFieldAliases = {
    row_id: ["row_id", "rowId", "id"],
    total_rooms: ["total_rooms", "totalRooms", "Total Rooms"],
    university_name: ["university_name", "universityName", "University Name"],
    country: ["country", "Country"],
    desk_number: ["desk_number", "deskNumber", "Desk Number"],
    staff: ["staff", "Staff"],
    connect_link: ["connect_link", "connectLink", "Connect Links"],
    crm_id: ["crm_id", "crmId", "CRM ID"],
};

const getFirstDefinedValue = (record, keys) => {
    for (const key of keys) {
        if (record[key] !== undefined && record[key] !== null) return record[key];
    }

    return "";
};

const normalizeScalar = (value) => {
    if (value === undefined || value === null) return "";
    if (typeof value === "number") return value;
    return String(value).trim();
};

export const normalizeUniversityLookupKey = (value) =>
    String(value || "")
        .toLowerCase()
        .replace(/&/g, " and ")
        .replace(/[^a-z0-9]+/g, " ")
        .trim()
        .replace(/\s+/g, " ");

export const normalizeUniversityRecord = (record, index = 0) => ({
    row_id: normalizeScalar(getFirstDefinedValue(record, universityFieldAliases.row_id)) || index + 1,
    total_rooms: normalizeScalar(getFirstDefinedValue(record, universityFieldAliases.total_rooms)),
    university_name:
        normalizeScalar(getFirstDefinedValue(record, universityFieldAliases.university_name)) || `University ${index + 1}`,
    country: normalizeScalar(getFirstDefinedValue(record, universityFieldAliases.country)),
    desk_number: normalizeScalar(getFirstDefinedValue(record, universityFieldAliases.desk_number)),
    staff: normalizeScalar(getFirstDefinedValue(record, universityFieldAliases.staff)),
    connect_link: normalizeScalar(getFirstDefinedValue(record, universityFieldAliases.connect_link)),
    crm_id: normalizeScalar(getFirstDefinedValue(record, universityFieldAliases.crm_id)),
});

export const buildCacheBustedUrl = (url, timestamp = Date.now()) => {
    const separator = url.includes("?") ? "&" : "?";
    return `${url}${separator}ts=${timestamp}`;
};

export const getUniversityRowKey = (record) => `${record.row_id}-${record.university_name}`;

const hiddenTotalRooms = new Set([1, 2, 53, 54, 55, 56]);

export const shouldDisplayUniversityRecord = (record) => {
    const totalRooms = Number(record.total_rooms);
    const normalizedName = String(record.university_name || "").trim().toLowerCase();

    if (hiddenTotalRooms.has(totalRooms)) return false;
    if (normalizedName === "tech support") return false;

    return true;
};

export const getUniversityFilterOptions = (records) =>
    Array.from(
        new Set(
            (records || [])
                .map((record) => String(record.university_name || "").trim())
                .filter(Boolean)
        )
    ).sort((first, second) => first.localeCompare(second));

export const filterUniversityRecordsBySelection = (records, selectedUniversityNames) => {
    if (!Array.isArray(selectedUniversityNames) || selectedUniversityNames.length === 0) {
        return records;
    }

    const selected = new Set(selectedUniversityNames);
    return (records || []).filter((record) => selected.has(record.university_name));
};

export const buildUniversityStaffMap = (records) => {
    const mapping = {};

    (records || []).forEach((record) => {
        const universityName = String(record.university_name || "").trim();
        const staffName = String(record.staff || "").trim();

        if (!universityName || !staffName) return;

        const normalizedUniversityName = normalizeUniversityLookupKey(universityName);
        if (!normalizedUniversityName) return;

        if (!mapping[normalizedUniversityName]) {
            mapping[normalizedUniversityName] = [];
        }

        if (!mapping[normalizedUniversityName].includes(staffName)) {
            mapping[normalizedUniversityName].push(staffName);
        }
    });

    return mapping;
};

export const getStaffNamesForStudentUniversities = (universityValue, universityStaffMap) => {
    const universities = String(universityValue || "-")
        .split(",")
        .map((item) => item.trim())
        .filter((item) => item && item !== "-");

    const staffNames = [];

    universities.forEach((universityName) => {
        const mappedStaffNames = universityStaffMap[normalizeUniversityLookupKey(universityName)] || [];
        mappedStaffNames.forEach((staffName) => {
            if (!staffNames.includes(staffName)) {
                staffNames.push(staffName);
            }
        });
    });

    return staffNames;
};

export const formatUniversitiesWithStaffNames = (universityValue, universityStaffMap) => {
    const universities = String(universityValue || "-")
        .split(",")
        .map((item) => item.trim())
        .filter((item) => item && item !== "-");

    if (universities.length === 0) return "-";

    return universities
        .map((universityName) => {
            const mappedStaffNames = universityStaffMap[normalizeUniversityLookupKey(universityName)] || [];
            return mappedStaffNames.length > 0
                ? `${universityName} (${mappedStaffNames.join(", ")})`
                : universityName;
        })
        .join(", ");
};

export const formatSingleUniversityWithStaffName = (universityName, universityStaffMap) => {
    const trimmedUniversityName = String(universityName || "").trim();
    if (!trimmedUniversityName || trimmedUniversityName === "-") return "-";

    const mappedStaffNames = universityStaffMap[normalizeUniversityLookupKey(trimmedUniversityName)] || [];
    return {
        universityName: trimmedUniversityName,
        staffText: mappedStaffNames.join(", "),
    };
};

export const filterItemsForUniversity = (items, universityName, getItemUniversityName) => {
    const normalizedTargetUniversity = normalizeUniversityLookupKey(universityName);
    if (!normalizedTargetUniversity || !Array.isArray(items)) return [];

    return items.filter((item) => {
        const itemUniversityName = getItemUniversityName(item);
        return normalizeUniversityLookupKey(itemUniversityName) === normalizedTargetUniversity;
    });
};

const getNormalizedUniversityCrmId = (value) => String(value || "").trim();

export const filterItemsForUniversityMatch = ({
    items,
    rowUniversityName,
    rowUniversityCrmId,
    studentAttendedUniversities,
    getItemUniversityName,
    getItemUniversityCrmId,
}) => {
    const normalizedRowUniversityCrmId = getNormalizedUniversityCrmId(rowUniversityCrmId);
    const normalizedRowUniversityName = normalizeUniversityLookupKey(rowUniversityName);
    if (!Array.isArray(items)) return [];

    if (normalizedRowUniversityCrmId) {
        const mappedUniversityNames = new Set(
            (Array.isArray(studentAttendedUniversities) ? studentAttendedUniversities : [])
                .filter((item) => getNormalizedUniversityCrmId(item.universityCrmId || item.university_crm_id || item.crmId) === normalizedRowUniversityCrmId)
                .map((item) => normalizeUniversityLookupKey(item.universityName))
                .filter(Boolean)
        );

        if (mappedUniversityNames.size > 0) {
            return items.filter((item) => {
                const itemUniversityCrmId = getNormalizedUniversityCrmId(
                    getItemUniversityCrmId ? getItemUniversityCrmId(item) : ""
                );
                if (itemUniversityCrmId) {
                    return itemUniversityCrmId === normalizedRowUniversityCrmId;
                }

                const itemUniversityName = normalizeUniversityLookupKey(getItemUniversityName(item));
                return itemUniversityName && mappedUniversityNames.has(itemUniversityName);
            });
        }
    }

    if (!normalizedRowUniversityName) return [];
    return items.filter((item) => {
        const itemUniversityName = normalizeUniversityLookupKey(getItemUniversityName(item));
        return itemUniversityName === normalizedRowUniversityName;
    });
};

export const getAttendedUniversitiesMissingNotes = (attendedUniversities, formattedNotes) => {
    const noteUniversityKeys = new Set(
        (Array.isArray(formattedNotes) ? formattedNotes : [])
            .map((note) => normalizeUniversityLookupKey(note.universityName))
            .filter(Boolean)
    );

    return (Array.isArray(attendedUniversities) ? attendedUniversities : []).filter((item) => {
        const normalizedName = normalizeUniversityLookupKey(item.universityName);
        return normalizedName && !noteUniversityKeys.has(normalizedName);
    });
};

export const shouldDisplayStudentCard = (student) => {
    const hasNotes = Array.isArray(student.formattedNotes) && student.formattedNotes.length > 0;
    const hasAttendedUniversity =
        Array.isArray(student.attendedUniversities) && student.attendedUniversities.length > 0;

    return hasNotes || hasAttendedUniversity;
};

const mapFallbackRecordToRawRow = (record) => ({
    "Total Rooms": record.total_rooms,
    "University Name": record.university_name,
    Country: record.country,
    "Desk Number": record.desk_number,
    Staff: record.staff,
    "Connect Links": record.connect_link,
    "CRM ID": record.crm_id,
});

const getRawRecordsFromPayload = (payload) =>
    Array.isArray(payload)
        ? payload
        : payload.data ||
          (Array.isArray(payload.sheets) && payload.sheets.length > 0 ? payload.sheets[0].rows : null);

export async function fetchInternalFairUniversitiesDirectory({
    jsonUrl,
    fetchImpl = (...args) => fetch(...args),
    fallbackData = fallbackUniversityDirectory,
}) {
    if (!jsonUrl) {
        return fallbackData
            .map((record, index) => normalizeUniversityRecord(record, index))
            .filter(shouldDisplayUniversityRecord);
    }

    const response = await fetchImpl(buildCacheBustedUrl(jsonUrl));
    if (!response.ok) {
        throw new Error("Unable to fetch internal fair university directory");
    }

    const payload = await response.json();
    const rawRecords = getRawRecordsFromPayload(payload);

    if (!Array.isArray(rawRecords)) {
        throw new Error("Internal fair university directory response is invalid");
    }

    return rawRecords
        .map((record, index) => normalizeUniversityRecord(record, index))
        .filter(shouldDisplayUniversityRecord);
}

export async function fetchInternalFairStaffingRows({
    jsonUrl,
    fetchImpl = (...args) => fetch(...args),
    fallbackData = fallbackUniversityDirectory,
}) {
    if (!jsonUrl) {
        return fallbackData.map(mapFallbackRecordToRawRow);
    }

    const response = await fetchImpl(buildCacheBustedUrl(jsonUrl));
    if (!response.ok) {
        throw new Error("Unable to fetch internal fair staffing rows");
    }

    const payload = await response.json();
    const rawRecords = getRawRecordsFromPayload(payload);

    if (!Array.isArray(rawRecords)) {
        throw new Error("Internal fair staffing response is invalid");
    }

    return rawRecords;
}

export async function fetchAllInternalFairUniversityStudents({
    fairId,
    universityName,
    universityCrmId = "",
    fetchReports,
}) {
    const loadFetchReports =
        fetchReports || require("../../../api/events").fetchInternalFairUniversityReportV2;

    const query = {
        fair_id: fairId,
    };

    if (String(universityCrmId || "").trim()) {
        query.university_crm_id = String(universityCrmId).trim();
    } else {
        query.university = universityName;
    }

    const response = await loadFetchReports(query);

    const records = Array.isArray(response.data) ? response.data : [];
    const totalCount = Number(response.headers.total_count || records.length || 0);
    const totalStudents = Number(response.headers.total_students_count || totalCount || 0);

    return {
        data: records,
        total: totalCount,
        totalStudents,
    };
}

export async function fetchAllInternalFairStudents({
    fairId,
    attendanceFilter = "all",
    university = "",
    studentName = "",
    uniqueCode = "",
    fetchReports,
    deserialize,
    pageSize = 100,
}) {
    const loadFetchReports =
        fetchReports || require("../../../api/events").fetchInternalFairReports;
    const loadDeserialize =
        deserialize || require("../../../utils/deserializers/reportsDeserializer").deserializeReport;

    let page = 1;
    let totalCount = 0;
    let totalStudents = 0;
    const records = [];

    do {
        const query = {
            fair_id: fairId,
            attendance_filter: attendanceFilter,
            page,
            per_page: pageSize,
        };

        if (String(university || "").trim()) query.university = String(university).trim();
        if (String(studentName || "").trim()) query.student_name = String(studentName).trim();
        if (String(uniqueCode || "").trim()) query.unique_code = String(uniqueCode).trim();

        const response = await loadFetchReports(query);

        const pageData = Array.isArray(response.data) ? response.data : [];
        pageData.forEach((item) => records.push(loadDeserialize(item, "counsellor")));

        totalCount = Number(response.headers.total_count || pageData.length || 0);
        totalStudents = Number(response.headers.total_students_count || totalStudents || 0);
        page += 1;
    } while (records.length < totalCount && totalCount > 0);

    return {
        data: records,
        total: totalCount,
        totalStudents,
    };
}
